import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import Application from "../../Application";
import ShowSuccessMessageInteractor from "../../interactor/notifications/ShowSuccessMessageInteractor";
import ShowErrorMessageInteractor from "../../interactor/notifications/ShowErrorMessageInteractor";
import { IMenuItem } from "../../model/menu/MenuItem";
import GetAllRootMenuItemsInteractor from "../../interactor/menu/GetAllRootMenuItemsInteractor";
import GetAvailableMenuItemsInteractor from "../../interactor/menu/GetAvailableMenuItemsInteractor";
import CreateMenuItemInteractor from "../../interactor/menu/CreateMenuItemInteractor";
import UpdateMenuItemInteractor from "../../interactor/menu/UpdateMenuItemInteractor";

export interface TMenuItemsPresenter extends TLoadingAwarePresenter {
  menuItems?: IMenuItem[];
  availableMenuItems?: IMenuItem[];
}

export interface IMenuItemsPresenter extends TMenuItemsPresenter, TPresentable {
  setMenuItems(menuItems?: IMenuItem[]): void;
  setAvailableMenuItems(availableMenuItems?: IMenuItem[]): void;
  createMenuItem(menuItem: IMenuItem): void;
  getAllMenuItems(): void;
  getAvailableMenuItems(menuItem?: IMenuItem): void;
  updateMenuItem(menuItem: IMenuItem): void;
  onStartUpdateHandler(rowData: any): void;
  onStartCreateHandler(rowData: any): void;
}

const defaultState: TMenuItemsPresenter = {
  menuItems: [],
  availableMenuItems: []
};

const MenuItemsPresenter = withStore<IMenuItemsPresenter, TMenuItemsPresenter>(
  ({ application, store, loader, translate }): IMenuItemsPresenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<TMenuItemsPresenter>();

    loader.start("menuItemsLoader");
    const setMenuItems = (menuItems: IMenuItem[]) => {
      _store.update({
        menuItems
      });
      loader.stop("menuItemsLoader");
    };

    const setAvailableMenuItems = (availableMenuItems: IMenuItem[]) => {
      _store.update({
        availableMenuItems
      });
    };

    const getAllMenuItems = async () => {
      try {
        let menuItems = await _application.container
          .resolve<GetAllRootMenuItemsInteractor>("getAllRootMenuItems")
          .execute();
        _store.update({
          menuItems
        });
        window.location.reload();
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const getAvailableMenuItems = async (
      menuItem: IMenuItem | undefined = undefined
    ) => {
      try {
        let availableMenuItems = await _application.container
          .resolve<GetAvailableMenuItemsInteractor>("getAvailableMenuItems")
          .execute(menuItem);

        _store.update({
          availableMenuItems
        });
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const onStartUpdateHandler = (rowData) => {
      getAvailableMenuItems(rowData);
    };

    const onStartCreateHandler = () => {
      getAvailableMenuItems();
    };

    const createMenuItem = async (menuItem: IMenuItem) => {
      try {
        await _application.container
          .resolve<CreateMenuItemInteractor>("createMenuItem")
          .execute(menuItem);
        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(
            translate("menu_items_page.messages.create_menu_item_success")
          );
        await getAllMenuItems();
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const updateMenuItem = async (menuItem: IMenuItem) => {
      try {
        await _application.container
          .resolve<UpdateMenuItemInteractor>("updateMenuItem")
          .execute(menuItem);
        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(
            translate("menu_items_page.messages.update_menu_item_success")
          );
        await getAllMenuItems();
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      setMenuItems,
      setAvailableMenuItems,
      createMenuItem,
      getAllMenuItems,
      getAvailableMenuItems,
      updateMenuItem,
      onStartUpdateHandler,
      onStartCreateHandler
    };
  },
  defaultState
);

export default MenuItemsPresenter;
